import { can } from './permissions.js';
/**
 * Appendix Z columns whose kind the appendix does not settle, and which are argued NOT to be money,
 * a cost or a margin. Keyed `reportId.columnKey`.
 *
 * `decisions/0009` decision 4 "fails closed": an unspecified column that is not named here counts as
 * money, so a report added later is financial until somebody argues otherwise, and this set is where
 * the arguing is written down. Each entry is a reading of an unlabelled column, not something the
 * appendix states, and an owner can empty the set to put all three reports behind
 * `report_financial`.
 *
 * - `lab_performance.breakage`: read as lenses broken at the lab, a count.
 * - `price_book_gaps.basis`, `.uncovered_interval`: Appendix V.4 names the pricing basis and the
 *   dioptre range with no price, which is where the price is missing, not a price.
 * - `ai_usage.credits`, `.remaining`: usage allowance units, not currency.
 *
 * Unspecified columns on a report that is financial anyway need no entry, and have none.
 */
export const UNSPECIFIED_COLUMNS_NOT_MONEY = new Set([
    'lab_performance.breakage',
    'price_book_gaps.basis',
    'price_book_gaps.uncovered_interval',
    'ai_usage.credits',
    'ai_usage.remaining',
]);
/**
 * `RPT-11` `MUST`: "Financial reports respect the permission matrix; operational reports are open
 * to all staff."
 *
 * `decisions/0009` decision 4: "A report is financial if any column it returns is money, a cost or a
 * margin. Everything else is operational: counts, ages, queues, turnaround times and volumes."
 *
 * A column is money-bearing when its kind is `money`, or when its kind is `unspecified` and it is
 * not cleared in {@link UNSPECIFIED_COLUMNS_NOT_MONEY}. Cost and margin columns are `money` in the
 * registry, so they are covered by the first half.
 */
export function classifyReport(report) {
    const bearsMoney = report.columns.some((c) => c.kind === 'money' ||
        (c.kind === 'unspecified' && !UNSPECIFIED_COLUMNS_NOT_MONEY.has(`${report.id}.${c.key}`)));
    return bearsMoney ? 'financial' : 'operational';
}
/** Appendix B's two report rows, one per class. */
export function permissionForClass(reportClass) {
    return reportClass === 'financial' ? 'report_financial' : 'report_operational';
}
/** `RPT-001`: the owner dashboard's seven figures. Not an Appendix Z row, so not in the registry. */
export const OWNER_DASHBOARD_METRICS = [
    'todays_takings',
    'jobs_due',
    'jobs_overdue',
    'uncollected_value',
    'outstanding_balances',
    'low_stock',
    'remake_count_this_month',
];
/** `RPT-10` (and `RPT-003`, which names CSV/XLSX and email only). */
export const REPORT_EXPORT_FORMATS = ['csv', 'xlsx'];
export const REPORT_SCHEDULE_CHANNELS = ['email', 'whatsapp'];
/** `V1-SPEC.md` §1 says 22. Appendix Z prints 21. Kept so a test can name the discrepancy. */
export const CLAIMED_REPORT_COUNT = 22;
const cols = (rows) => rows.map(([key, label, kind, named]) => ({
    key,
    label,
    kind,
    typeBasis: named === true
        ? 'named'
        : kind === 'unspecified'
            ? 'unspecified'
            : 'inferred',
}));
const params = (...keys) => keys.map((key) => ({ key }));
const STOCK_AGEING_COLUMNS = cols([
    ['product', 'product', 'dimension'],
    ['qty', 'qty', 'quantity'],
    ['cost_value', 'cost value', 'money'],
    ['days_on_hand', 'days on hand', 'days', true],
    ['last_sold', 'last sold', 'unspecified'],
]);
const def = (d) => {
    const reportClass = classifyReport(d);
    return {
        ...d,
        grain: 'unspecified',
        defaultSort: 'unspecified',
        chart: 'unspecified',
        reportClass,
        permission: permissionForClass(reportClass),
        permissionBasis: 'rule',
    };
};
/** Appendix Z in table order, 21 rows. */
export const REPORT_DEFINITIONS = [
    def({
        id: 'daily_takings',
        title: 'Daily takings',
        columns: cols([
            ['tender', 'tender', 'dimension'],
            ['count', 'count', 'quantity', true],
            ['gross', 'gross', 'money'],
            ['tax', 'tax', 'money'],
            ['net', 'net', 'money'],
        ]),
        parameters: params('branch', 'staff', 'date'),
        calculation: 'by cash session',
    }),
    def({
        id: 'sales_summary',
        title: 'Sales summary',
        columns: cols([
            ['period', 'period', 'dimension'],
            ['orders', 'orders', 'quantity'],
            ['units', 'units', 'quantity'],
            ['gross', 'gross', 'money'],
            ['discount', 'discount', 'money'],
            ['net', 'net', 'money'],
            ['tax', 'tax', 'money'],
        ]),
        parameters: params('branch', 'staff', 'category', 'channel'),
    }),
    def({
        id: 'gross_margin',
        title: 'Gross margin',
        columns: cols([
            ['product_or_category', 'product or category', 'dimension'],
            ['units', 'units', 'quantity'],
            ['revenue', 'revenue', 'money'],
            ['cogs', 'COGS', 'money'],
            ['margin', 'margin', 'money'],
            ['margin_pct', 'margin %', 'percent', true],
        ]),
        parameters: params('branch', 'brand', 'lens_product'),
        calculation: 'COGS at collection, weighted average cost',
    }),
    def({
        id: 'lens_asp',
        title: 'Lens ASP',
        columns: cols([
            ['lens_tier', 'lens tier', 'dimension'],
            ['pairs', 'pairs', 'quantity'],
            ['average_selling_price', 'average selling price', 'money'],
        ]),
        parameters: params('branch', 'staff'),
        calculation: 'revenue ÷ pairs, lens lines only',
    }),
    def({
        id: 'attach_rate',
        title: 'Attach rate',
        columns: cols([
            ['dispenses', 'dispenses', 'quantity'],
            ['with_second_pair', 'with second pair', 'quantity'],
            ['with_premium_coating', 'with premium coating', 'quantity'],
            ['pct_each', '% each', 'percent', true],
        ]),
        parameters: params('branch', 'staff'),
    }),
    def({
        id: 'discount_leakage',
        title: 'Discount leakage',
        columns: cols([
            ['staff', 'staff', 'dimension'],
            ['count', 'count', 'quantity', true],
            ['value', 'value', 'money'],
            ['pct_of_gross', '% of gross', 'percent', true],
            ['reason_breakdown', 'reason breakdown', 'unspecified'],
        ]),
        parameters: params('branch', 'period'),
        calculation: 'discount value ÷ gross before discount',
    }),
    def({
        id: 'stock_ageing',
        title: 'Stock ageing',
        columns: STOCK_AGEING_COLUMNS,
        parameters: params('branch', 'brand', 'band'),
        calculation: 'days since goods receipt',
    }),
    def({
        id: 'dead_stock',
        title: 'Dead stock',
        // "as above, filtered beyond threshold": the Stock ageing columns. Group/filter is blank.
        columns: STOCK_AGEING_COLUMNS,
        parameters: [{ key: 'threshold_days', defaultValue: 180 }],
        calculation: 'default 180 days no sale',
    }),
    def({
        id: 'stock_valuation',
        title: 'Stock valuation',
        columns: cols([
            ['category', 'category', 'dimension'],
            ['qty', 'qty', 'quantity'],
            ['cost_value', 'cost value', 'money'],
            ['owned_vs_consignment', 'owned vs consignment', 'unspecified'],
        ]),
        parameters: params('branch'),
        calculation: 'consignment excluded from owned',
    }),
    def({
        id: 'reorder_suggestion',
        title: 'Reorder suggestion',
        columns: cols([
            ['product', 'product', 'dimension'],
            ['on_hand', 'on hand', 'quantity'],
            ['sold_in_period', 'sold in period', 'quantity'],
            ['suggested_qty', 'suggested qty', 'quantity'],
        ]),
        parameters: params('branch', 'supplier'),
        calculation: 'sell-through, not min/max',
    }),
    def({
        id: 'lab_performance',
        title: 'Lab performance',
        columns: cols([
            ['lab', 'lab', 'dimension'],
            ['jobs', 'jobs', 'quantity'],
            ['on_time_pct', 'on-time %', 'percent', true],
            ['average_days', 'average days', 'days', true],
            ['remake_pct', 'remake %', 'percent', true],
            ['breakage', 'breakage', 'unspecified'],
        ]),
        parameters: params('period'),
        calculation: 'promised vs received',
    }),
    def({
        id: 'remake_analysis',
        title: 'Remake analysis',
        columns: cols([
            ['cause', 'cause', 'dimension'],
            ['count', 'count', 'quantity', true],
            ['customer_charge', 'customer charge', 'money'],
            ['shop_cost', 'shop cost', 'money'],
            ['supplier_credit', 'supplier credit', 'money'],
            ['net', 'net', 'money'],
        ]),
        parameters: params('staff', 'lens_product', 'lab'),
        calculation: 'four amounts per §6.9 `RMK-003`',
    }),
    def({
        id: 'uncollected_jobs',
        title: 'Uncollected jobs',
        columns: cols([
            ['job', 'job', 'dimension'],
            ['customer', 'customer', 'dimension'],
            ['ready_date', 'ready date', 'date', true],
            ['days', 'days', 'days', true],
            ['value', 'value', 'money'],
            ['balance', 'balance', 'money'],
        ]),
        parameters: params('branch'),
        calculation: 'ready to now',
    }),
    def({
        id: 'outstanding_balances',
        title: 'Outstanding balances',
        columns: cols([
            ['customer', 'customer', 'dimension'],
            ['balance', 'balance', 'money'],
            ['oldest_item', 'oldest item', 'unspecified'],
            ['days', 'days', 'days', true],
        ]),
        parameters: params('branch'),
        calculation: 'udhaar plus order balances',
    }),
    def({
        id: 'panel_statement',
        title: 'Panel statement',
        columns: cols([
            ['panel', 'panel', 'dimension'],
            ['orders', 'orders', 'quantity'],
            ['panel_share', 'panel share', 'unspecified'],
            ['customer_share', 'customer share', 'unspecified'],
            ['paid', 'paid', 'money'],
            ['outstanding', 'outstanding', 'money'],
        ]),
        parameters: params('period'),
    }),
    def({
        id: 'supplier_ageing',
        title: 'Supplier ageing',
        columns: cols([
            ['supplier', 'supplier', 'dimension'],
            ['invoices', 'invoices', 'quantity'],
            ['due', 'due', 'money'],
            ['overdue_buckets', 'overdue buckets', 'unspecified'],
        ]),
        parameters: [],
    }),
    def({
        id: 'commission',
        title: 'Commission',
        columns: cols([
            ['staff', 'staff', 'dimension'],
            ['qualifying_lines', 'qualifying lines', 'quantity'],
            ['basis', 'basis', 'unspecified'],
            ['earned', 'earned', 'money'],
            ['clawback', 'clawback', 'money'],
            ['net', 'net', 'money'],
        ]),
        parameters: params('period', 'branch'),
        calculation: '§6.24',
    }),
    def({
        id: 'recall_response',
        title: 'Recall response',
        columns: cols([
            ['campaign', 'campaign', 'dimension'],
            ['sent', 'sent', 'quantity'],
            ['delivered', 'delivered', 'quantity'],
            ['responded', 'responded', 'quantity'],
            ['converted', 'converted', 'quantity'],
            ['revenue', 'revenue', 'money'],
        ]),
        parameters: [],
    }),
    def({
        id: 'correction_log',
        title: 'Correction log',
        columns: cols([
            ['type', 'type', 'dimension'],
            ['count', 'count', 'quantity', true],
            ['staff', 'staff', 'dimension'],
            ['value_moved', 'value moved', 'money'],
        ]),
        parameters: params('period'),
        calculation: 'training and fraud signal',
    }),
    def({
        id: 'price_book_gaps',
        title: 'Price book gaps',
        columns: cols([
            ['product', 'product', 'dimension'],
            ['basis', 'basis', 'unspecified'],
            ['uncovered_interval', 'uncovered interval', 'unspecified'],
        ]),
        parameters: params('price_book_version'),
        calculation: 'from Appendix V.4',
    }),
    def({
        id: 'ai_usage',
        title: 'AI usage',
        columns: cols([
            ['task', 'task', 'dimension'],
            ['count', 'count', 'quantity', true],
            ['credits', 'credits', 'unspecified'],
            ['remaining', 'remaining', 'unspecified'],
        ]),
        parameters: params('tenant', 'period'),
    }),
];
const BY_ID = new Map(REPORT_DEFINITIONS.map((d) => [d.id, d]));
export function getReport(id) {
    const d = BY_ID.get(id);
    if (d === undefined)
        throw new Error(`Unknown report: ${String(id)}`);
    return d;
}
/** `RPT-11`: may this role run this report? Branch scope is checked elsewhere (`ROLE-001`). */
export function canRunReport(role, id) {
    return can(role, getReport(id).permission);
}
/** The reports a role can run, in Appendix Z order. */
export function reportsForRole(role) {
    return REPORT_DEFINITIONS.filter((d) => can(role, d.permission));
}
//# sourceMappingURL=reports.js.map